/**
 * Tokens de cor — extraídos de "Viajandos — Arquitetura e Design System" (v2;
 * arquivo `design/project/Viajandos - Arquitetura e Design System.dc.html`).
 *
 * Regra do design: os acentos são IDÊNTICOS nos dois temas porque carregam
 * significado. Só os neutros trocam.
 *
 * Regra de contraste: texto sobre acento sempre em tinta escura com opacidade
 * total. Nunca branco sobre coral (2,9:1), nunca texto esmaecido sobre acento.
 * Por isso cada acento vem com o seu `ink` — use sempre o par, nunca escolha
 * a cor do texto à mão.
 */

/**
 * Acentos — fixos nos dois temas, nomeados pelo papel.
 *
 * A marca é o laranja medido no ícone aprovado (`#F8481D`, 75% dos pixels);
 * os demais foram reafinados para conviver com ele. Os nomes de cor
 * (`amber`, `coral`, `mint`, `sky`, `orange`) não existem mais: cor é
 * consequência do papel, e um acento que troca de valor não troca de nome.
 */
export const accent = {
  /** Ação primária, aba ativa, anel de foco, card AGORA, marca. Medido no ícone. */
  brand: '#F8481D',
  /** Dinheiro/dívida, ação destrutiva, erro, localização antiga. */
  danger: '#B3123C',
  /** Grupo, concluído, valor a receber, sincronizado, toggle ligado. */
  success: '#0FB981',
  /** Pendência de sincronização, aviso, fora de ordem cronológica. */
  warn: '#F5B301',
  /** Lugares, hospedagem, transporte, informação neutra. */
  info: '#3D9BFF',
  /** Trilha paralela, compras, categoria. */
  violet: '#9B7BF7',
} as const;

/**
 * Tinta obrigatória sobre cada acento. Use sempre o par, nunca escolha a
 * cor do texto à mão. `danger` (carmim, 6,84:1) e `brand` têm tinta branca.
 *
 * `brand` em branco é decisão do PO (19/09/2026, lote de aparência 6):
 * botões e seleções laranja com texto branco em todo o app, pela leitura
 * "de férias" da marca — sabendo que mede 3,53:1 (abaixo do AA para texto
 * pequeno). Texto sobre a marca é sempre bold e ≥ 13 px para compensar.
 */
export const onAccent = {
  brand: '#FFFFFF',
  danger: '#FFFFFF',
  success: '#141414',
  warn: '#141414',
  info: '#141414',
  violet: '#141414',
} as const;

/**
 * Cores que só fazem sentido como TEXTO — o laranja é forte demais para ser
 * tinta de letra sobre fundo claro (link escurece para 5,4:1 sobre --surf).
 */
export const link = {
  light: '#B3300F',
  dark: '#FF7A52',
} as const;

/** Texto de status sobre --chip no Dark, onde o acento sólido não cabe. */
export const statusOnChipDark = {
  success: '#3FD9A4',
  danger: '#FF8AA3',
} as const;

export type AccentName = keyof typeof accent;

/**
 * Pares tint/ink de cada acento — refino visual final (handoff 23/09/2026,
 * direção "1a · Arejado"). `tint` é o fundo claro de ícone, chip, pílula ou
 * faixa; `ink` é o texto na mesma família sobre esse fundo (ou sobre o
 * branco). Nunca texto em acento sólido sobre o próprio tint.
 *
 * Os acentos sólidos continuam iguais nos dois temas; os tints e inks do
 * escuro são uma primeira aproximação (translúcidos sobre o fundo escuro) e
 * serão afinados no PR de dark mode.
 */
export const accentTone = {
  light: {
    /** Link e texto laranja sobre fundo claro — o mesmo `link.light`. */
    brandInk: '#B3300F',
    /** Valor em destaque (1º do ranking). */
    brandDeep: '#D83A12',
    /** Fundo de ícone/chip da marca, faixa "Sua parte". */
    brandTint: '#FFF1EC',
    /** Etiqueta Top comprador, opção votada. */
    brandTint2: '#FFE4DA',
    successInk: '#0B6B4B',
    successTint: '#E6F8F1',
    warnInk: '#8A6100',
    warnTint: '#FFF4D6',
    dangerTint: '#FBE3E8',
  },
  dark: {
    brandInk: '#FF7A52',
    brandDeep: '#FF7A52',
    brandTint: 'rgba(248,72,29,0.16)',
    brandTint2: 'rgba(248,72,29,0.26)',
    successInk: '#3FD9A4',
    successTint: 'rgba(15,185,129,0.16)',
    warnInk: '#F5C543',
    warnTint: 'rgba(245,179,1,0.16)',
    dangerTint: 'rgba(255,138,163,0.14)',
  },
} as const;

/**
 * Paleta de famílias. É uma LISTA, não um mapa de nomes: quem cria a família
 * escolhe a cor, e a viagem de referência não pode vazar para o produto
 * (regra de genericidade, ROADMAP §1.2).
 *
 * Lista PRÓPRIA, não alias de `accent`. Cor semântica e cor de família são
 * coisas diferentes: um acento pode escurecer a ponto de pedir tinta branca
 * (é o caso do novo `danger`), mas o avatar de família é sempre inicial em
 * tinta escura sobre a cor. Enquanto as duas listas eram o mesmo array,
 * retunar um acento quebrava um avatar. Os valores, por ora, são os mesmos —
 * este passo só desfaz o acoplamento.
 */
export const familyPalette = [
  '#F5B301', // âmbar
  '#E0405F', // carmim — claro o bastante para ink, ao contrário de accent.danger
  '#0FB981', // menta
  '#9B7BF7', // violeta
  '#3D9BFF', // céu
  '#0EA5A5', // teal — no lugar do laranja antigo, perto demais da marca
] as const;

/**
 * Cores do DIA do roteiro (teste físico, item 28): oito, por nome, para a
 * pessoa escolher o tema visual do dia. Nomes de cor, não de destino.
 */
export const dayPalette = {
  coral: '#FF6B4A',
  laranja: '#F5A524',
  amarelo: '#F5B301',
  verde: '#0FB981',
  azul: '#3D9BFF',
  violeta: '#9B7BF7',
  rosa: '#E0405F',
  cinza: '#8A8A8A',
} as const;

/**
 * Tons da Etiqueta (pílula do ranking e da categoria de despesa — refino
 * final, 23/09/2026). Cada tom é um par fundo/texto que já passa AA; a
 * etiqueta nunca escolhe a cor do texto à mão. Nomes de cor, não de título:
 * quem decide qual título usa qual tom é a tela.
 */
export const labelTone = {
  coral: { bg: '#FFE4DA', fg: '#D83A12' },
  ambar: { bg: '#FFF1C7', fg: '#8A6100' },
  lilas: { bg: '#EEE8FE', fg: '#5B3FC4' },
  verde: { bg: '#D5F5E9', fg: '#0B6B4B' },
  teal: { bg: '#D4F1F1', fg: '#0B6E6E' },
  azul: { bg: '#DCEBFF', fg: '#1668C4' },
  rosa: { bg: '#FBE3E8', fg: '#B3123C' },
  tinta: { bg: '#141414', fg: '#FFFFFF' },
} as const;

export type LabelTone = keyof typeof labelTone;

/** Tinta sobre qualquer cor de família: o avatar é inicial em tinta escura. */
export const onFamily = '#141414';

/** Preto neutro-quente, fiel ao ink medido — não o azul-escuro do protótipo antigo. */
const neutralsDark = {
  bg: '#100F0E',
  surf: '#1A1917',
  surf2: '#232120',
  /** Refino final: .08 (era .13) — no escuro a profundidade vem do tom da
   *  superfície, não da borda. */
  line: 'rgba(255,255,255,0.08)',
  ink: '#FFFFFF',
  /** Corpo longo. */
  ink2: 'rgba(255,255,255,0.82)',
  mut: 'rgba(255,255,255,0.60)',
  /** CTA desabilitado, item riscado. */
  disabled: 'rgba(255,255,255,0.38)',
  chip: 'rgba(255,255,255,0.08)',
  /** Trilho de barra, CTA desabilitado. */
  track: 'rgba(255,255,255,0.10)',
  /** Borda de campo sem foco. */
  fieldBorder: 'rgba(255,255,255,0.16)',
} as const;

/** Off-white com um sopro de calor do laranja — papel, não cinza de escritório. */
const neutralsLight = {
  bg: '#FFFFFF', // branco absoluto (PO, 18/09, lote 3) — os cards se separam pela linha, não pelo fundo
  surf: '#FFFFFF',
  /** Card de nota, "Não tem conta?" (refino final: era #F1EDE9, que virou `track`). */
  surf2: '#F6F3F0',
  /** Refino final: .10 (era .12) — o card se separa pelo fio, mais leve. */
  line: 'rgba(20,20,20,0.10)',
  ink: '#141414',
  ink2: '#3F3A35',
  mut: '#6B6560',
  disabled: '#8A837C',
  chip: 'rgba(20,20,20,0.055)',
  track: '#F1EDE9',
  fieldBorder: 'rgba(20,20,20,0.14)',
} as const;

/** Overlay (scrim) dos bottom sheets — igual nos dois temas. Refino final: .5. */
export const overlay = 'rgba(4,6,20,0.5)';

export type ThemeName = 'dark' | 'light';

/** As chaves são fixas nos dois temas; os valores mudam. */
export type ColorTheme = {
  readonly [K in keyof typeof neutralsDark]: string;
} & {
  readonly accent: typeof accent;
  readonly onAccent: typeof onAccent;
  readonly overlay: string;
  /** Cor de texto-link deste tema. */
  readonly link: string;
  /** Pares tint/ink dos acentos neste tema. */
  readonly tone: { readonly [K in keyof typeof accentTone.light]: string };
};

export const colors: Record<ThemeName, ColorTheme> = {
  dark: { ...neutralsDark, accent, onAccent, overlay, link: link.dark, tone: accentTone.dark },
  light: { ...neutralsLight, accent, onAccent, overlay, link: link.light, tone: accentTone.light },
};
